import { useMemo } from 'react';
import axios from 'axios';
import { keepPreviousData, useInfiniteQuery, type QueryKey } from '@tanstack/react-query';
import type { PaginatedResponse } from '@/types';

const PAGE_SIZE = 100;

/** The requested run / margin call does not exist: retrying cannot succeed. */
export const isHistoryItemNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

/** The By-counterparty selection key of a (counterparty, currency) book, as `?sel=` carries it. */
export const bookKey = (book: { counterparty_name: string; currency: string }): string =>
  `${book.counterparty_name}|${book.currency}`;

/** The book a `?sel=` key names, or undefined for anything that is not `<name>|<currency>`. */
export const parseBookKey = (
  key: string | undefined
): { counterparty_name: string; currency: string } | undefined => {
  const at = key?.lastIndexOf('|') ?? -1;
  if (!key || at <= 0 || at === key.length - 1) return undefined;
  return { counterparty_name: key.slice(0, at), currency: key.slice(at + 1) };
};

interface UseHistoryPagesOptions<T> {
  queryKey: QueryKey;
  fetchPage: (limit: number, offset: number) => Promise<PaginatedResponse<T>>;
  /** Identity of a row, so one that slid across a page boundary under a new write shows once.
   *  Pass a module-level function: it is a memo dependency. */
  keyOf: (item: T) => string | number;
  enabled?: boolean;
  /** Keep the previous key's rows on screen while a new key loads (a rail under a new search). */
  keepPrevious?: boolean;
}

/**
 * A History feed paged on the server by row offset. `items` is the loaded window, deduped;
 * `totalCount` the server's total for the current filters (undefined until the first page lands).
 */
export function useHistoryPages<T>({
  queryKey,
  fetchPage,
  keyOf,
  enabled = true,
  keepPrevious = false,
}: UseHistoryPagesOptions<T>) {
  const query = useInfiniteQuery({
    // A disabled feed reads nothing. Its filters may hash to another feed's key (`{}` and
    // `{search: undefined}` are one key, the whole log), and a disabled observer still hands
    // back that query's cached rows.
    queryKey: enabled ? queryKey : [...queryKey, 'disabled'],
    // A row cursor, not a page index: a short page would make `index * PAGE_SIZE` skip rows.
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: number }) => fetchPage(PAGE_SIZE, pageParam),
    getNextPageParam: (lastPage, allPages) => {
      // An empty page ends the paging whatever `count` says (it can drift under a concurrent write).
      if (lastPage.results.length === 0) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    // Not into the disabled key: `keepPreviousData` ignores `enabled` and would carry the rows over.
    placeholderData: keepPrevious && enabled ? keepPreviousData : undefined,
    enabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const items = useMemo(() => {
    const seen = new Set<string | number>();
    const out: T[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const item of page.results) {
        const key = keyOf(item);
        if (!seen.has(key)) {
          seen.add(key);
          out.push(item);
        }
      }
    }
    return out;
  }, [query.data, keyOf]);

  return { ...query, items, totalCount: query.data?.pages[0]?.count };
}
